import React from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../../store/authStore'
import { useActiveTenantId } from '../../../store/tenantStore'
import { useDashboardQuery } from '../hooks/useDashboardQuery'
import { Card, Badge, Skeleton, PageHeader } from '@biartbt/biartd'

const Dashboard: React.FC = () => {
  const { t } = useTranslation()
  const user = useAuthStore((state) => state.user)
  const tenantId = useActiveTenantId()
  const { data: stats, isLoading, isError } = useDashboardQuery(tenantId)

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('dashboard.title')}
        subtitle={`${t('common.welcome')}, ${user?.name || 'User'}!`}
      />

      {/* Stats Cards Row 1 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('dashboard.stats.totalEmployees')}
          value={stats?.totalEmployees}
          icon={<UsersIcon />}
          color="blue"
          isLoading={isLoading}
        />
        <StatCard
          title={t('dashboard.stats.activeEmployees')}
          value={stats?.activeEmployees}
          icon={<UserCheckIcon />}
          color="green"
          isLoading={isLoading}
        />
        <StatCard
          title={t('dashboard.stats.totalDepartments')}
          value={stats?.totalDepartments}
          icon={<BuildingIcon />}
          color="purple"
          isLoading={isLoading}
        />
        <StatCard
          title={t('dashboard.stats.totalOrganizations')}
          value={stats?.totalOrganizations}
          icon={<OrgIcon />}
          color="amber"
          isLoading={isLoading}
        />
      </div>

      {/* Stats Cards Row 2 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('dashboard.stats.totalUsers')}
          value={stats?.totalUsers}
          icon={<KeyIcon />}
          color="indigo"
          isLoading={isLoading}
        />
        <StatCard
          title={t('dashboard.stats.activeUsers')}
          value={stats?.activeUsers}
          icon={<ShieldIcon />}
          color="teal"
          isLoading={isLoading}
        />
        <StatCard
          title={t('dashboard.stats.inactiveEmployees')}
          value={stats?.inactiveEmployees}
          icon={<UserXIcon />}
          color="red"
          isLoading={isLoading}
        />
        <StatCard
          title={t('dashboard.stats.employeeRate')}
          value={
            stats && stats.totalEmployees > 0
              ? `${Math.round((stats.activeEmployees / stats.totalEmployees) * 100)}%`
              : '—'
          }
          icon={<ChartIcon />}
          color="emerald"
          isLoading={isLoading}
        />
      </div>

      {/* Bottom Section: Department Distribution + Recent Employees */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Department Distribution */}
        <Card>
          <div className="p-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('dashboard.departmentDistribution')}
            </h3>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : stats?.departmentDistribution && stats.departmentDistribution.length > 0 ? (
              <div className="space-y-3">
                {stats.departmentDistribution.map((dept) => {
                  const maxCount = stats.departmentDistribution[0]?.employeeCount || 1
                  const pct = Math.round((dept.employeeCount / maxCount) * 100)
                  return (
                    <div key={dept.departmentId}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[200px]">
                          {dept.departmentName}
                        </span>
                        <Badge variant="secondary">{dept.employeeCount}</Badge>
                      </div>
                      <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('dashboard.noData')}
              </p>
            )}
          </div>
        </Card>

        {/* Recent Employees */}
        <Card>
          <div className="p-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('dashboard.recentEmployees')}
            </h3>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : stats?.recentEmployees && stats.recentEmployees.length > 0 ? (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {stats.recentEmployees.map((emp) => (
                  <div key={emp.employeeId} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                        {emp.name.charAt(0)}{emp.surname?.charAt(0) || ''}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {emp.name} {emp.surname || ''}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {emp.email || '—'}
                      </p>
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                      {emp.hireDate
                        ? new Date(emp.hireDate).toLocaleDateString()
                        : new Date(emp.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('dashboard.noData')}
              </p>
            )}
          </div>
        </Card>
      </div>

      {/* Error State */}
      {isError && (
        <Card>
          <div className="p-5 text-center text-sm text-red-600 dark:text-red-400">
            {t('dashboard.errorLoading')}
          </div>
        </Card>
      )}
    </div>
  )
}

export default Dashboard

/* ─── Stat Card ──────────────────────────────────────── */

const colorMap: Record<string, { bg: string; icon: string; text: string }> = {
  blue:    { bg: 'bg-blue-50 dark:bg-blue-900/30',    icon: 'text-blue-600 dark:text-blue-400',    text: 'text-blue-700 dark:text-blue-300' },
  green:   { bg: 'bg-green-50 dark:bg-green-900/30',   icon: 'text-green-600 dark:text-green-400',   text: 'text-green-700 dark:text-green-300' },
  purple:  { bg: 'bg-purple-50 dark:bg-purple-900/30',  icon: 'text-purple-600 dark:text-purple-400',  text: 'text-purple-700 dark:text-purple-300' },
  amber:   { bg: 'bg-amber-50 dark:bg-amber-900/30',   icon: 'text-amber-600 dark:text-amber-400',   text: 'text-amber-700 dark:text-amber-300' },
  indigo:  { bg: 'bg-indigo-50 dark:bg-indigo-900/30',  icon: 'text-indigo-600 dark:text-indigo-400',  text: 'text-indigo-700 dark:text-indigo-300' },
  teal:    { bg: 'bg-teal-50 dark:bg-teal-900/30',    icon: 'text-teal-600 dark:text-teal-400',    text: 'text-teal-700 dark:text-teal-300' },
  red:     { bg: 'bg-red-50 dark:bg-red-900/30',     icon: 'text-red-600 dark:text-red-400',     text: 'text-red-700 dark:text-red-300' },
  emerald: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', icon: 'text-emerald-600 dark:text-emerald-400', text: 'text-emerald-700 dark:text-emerald-300' },
}

function StatCard({
  title,
  value,
  icon,
  color,
  isLoading,
}: {
  title: string
  value?: number | string
  icon: React.ReactNode
  color: string
  isLoading: boolean
}) {
  const c = colorMap[color] || colorMap.blue
  return (
    <Card>
      <div className="p-5 flex items-center gap-4">
        <div className={`flex-shrink-0 w-12 h-12 rounded-xl ${c.bg} flex items-center justify-center`}>
          <span className={c.icon}>{icon}</span>
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide truncate">
            {title}
          </p>
          {isLoading ? (
            <Skeleton className="h-7 w-16 mt-1" />
          ) : (
            <p className={`text-2xl font-bold ${c.text} mt-0.5`}>
              {value ?? '—'}
            </p>
          )}
        </div>
      </div>
    </Card>
  )
}

/* ─── Inline SVG Icons ───────────────────────────────── */

const iconClass = 'w-5 h-5'

function UsersIcon() {
  return (
    <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
    </svg>
  )
}

function UserCheckIcon() {
  return (
    <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="m16.5 11.25 2.25 2.25L21 11.25" />
    </svg>
  )
}

function UserXIcon() {
  return (
    <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M22 10.5h-6m4.5-6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
    </svg>
  )
}

function BuildingIcon() {
  return (
    <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
    </svg>
  )
}

function OrgIcon() {
  return (
    <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21m-3.75 3H21" />
    </svg>
  )
}

function KeyIcon() {
  return (
    <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
    </svg>
  )
}

function ShieldIcon() {
  return (
    <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
    </svg>
  )
}

function ChartIcon() {
  return (
    <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
    </svg>
  )
}
